import { Box, CircularProgress, Divider, Paper, Skeleton, Typography } from '@mui/material';
import { ReactNode } from 'react';

type TotalEntry = { label: string; value: number | string };

type Props = {
  title: string;
  subtitle?: string;
  totals?: TotalEntry[];
  rightAction?: ReactNode;
  isLoading?: boolean;
  isFetching?: boolean;
  isEmpty?: boolean;
  emptyMessage?: string;
  footer?: ReactNode;
  chart: ReactNode;
  chartHeight?: number;
};

export const AnalyticsCard = ({
  title,
  subtitle,
  totals,
  rightAction,
  isLoading,
  isFetching,
  isEmpty,
  emptyMessage = 'No data for the selected range',
  footer,
  chart,
  chartHeight = 280,
}: Props) => (
  <Paper sx={{ p: 2, mb: 2 }}>
    <Box display="flex" alignItems="flex-start" gap={2}>
      <Box flex={1}>
        <Typography variant="subtitle1" fontWeight={500}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      {rightAction}
    </Box>

    {totals && totals.length > 0 && !isLoading && (
      <Box display="flex" gap={4} mt={1.5}>
        {totals.map(({ label, value }) => (
          <Box key={label}>
            <Typography variant="h5" fontWeight={500} lineHeight={1.2}>
              {value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
          </Box>
        ))}
      </Box>
    )}

    <Divider sx={{ my: 1.5 }} />

    <Box sx={{ height: chartHeight, position: 'relative' }}>
      {isLoading ? (
        <Skeleton variant="rectangular" height={chartHeight} sx={{ borderRadius: 1 }} />
      ) : isEmpty ? (
        <Box height="100%" display="flex" alignItems="center" justifyContent="center" color="text.secondary">
          {emptyMessage}
        </Box>
      ) : (
        <Box sx={{ height: '100%', opacity: isFetching ? 0.5 : 1, transition: 'opacity 0.15s' }}>{chart}</Box>
      )}
      {isFetching && !isLoading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.4)',
            backdropFilter: 'blur(1px)',
            zIndex: 1,
            pointerEvents: 'none',
          }}
        >
          <CircularProgress size={40} thickness={4} />
        </Box>
      )}
    </Box>

    {footer && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {footer}
      </Typography>
    )}
  </Paper>
);
